import React, { useEffect, useCallback, useState, useRef } from 'react';
import styled from 'styled-components';
import Cookie from 'js-cookie';

import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';
import { increment } from './utils/logToDataDog';
import { getWindow } from './utils/getWindow';

export default function Accessibility(props) {
    const { appNameForMetrics, sendMetric } = props;

    const [shouldLoadAcsb, setShouldLoadAcsb] = useState(
        Boolean(Cookie.get('zd-accessibility'))
    );
    const buttonRef = useRef(null);
    useFocusFirstEffect(buttonRef);

    useEffect(() => {
        // Don't show the AccessiBe panel if the user refreshes the page
        if (window.location.hash === '#showacsb') {
            window.location.hash = '';
        }
    }, []);

    useEffect(() => {
        // AccessiBe will fail to load unless we're on prod
        const isProd =
            getWindow().location.hostname.indexOf('www.zocdoc.com') > -1;

        if (shouldLoadAcsb && isProd) {
            appendAccessibeScript();
        }
    }, [shouldLoadAcsb]);

    const handleFocusButton = useCallback(() => {
        sendMetric({
            element: 'View Accessibility Mode Button',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
        increment({
            name: `${appNameForMetrics}.accessibility.view_accessibility_mode_button`,
        });
    }, [appNameForMetrics, sendMetric]);

    const handleClickButton = useCallback(() => {
        Cookie.set('zd-accessibility', 'on', { expires: 1 }); // Store a session cookie
        window.location.hash = 'showacsb'; // Force open the accessiBe panel
        setShouldLoadAcsb(true);
        sendMetric({
            element: 'Enable Accessibility Mode Button',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        increment({
            name: `${appNameForMetrics}.accessibility.enable_accessibility_mode`,
        });
    }, [appNameForMetrics, sendMetric]);

    return shouldLoadAcsb ? null : (
        <StyledButton
            data-test="enable-accessibility-button"
            onClick={handleClickButton}
            onFocus={handleFocusButton}
            ref={buttonRef}
            type="button"
        >
            Use this website in accessibility mode
        </StyledButton>
    );
}

/**
 * Returns a ref which gets focused first when the user presses tab if no other element is focused.
 */
function useFocusFirstEffect(ref) {
    useEffect(() => {
        function handleKeyDown(e) {
            // If the user presses Tab with no element focused,
            // focus the Accessibility mode button first.
            if (
                document.activeElement === document.body &&
                e.key === 'Tab' &&
                ref.current
            ) {
                const enableAccessibilityButton = document.querySelector(
                    '[data-test="enable-accessibility-button"]'
                );

                // Make sure the button isn't currently hidden (e.g. a modal is open)
                const isButtonHidden = Array.prototype.some.call(
                    document.body.querySelectorAll('[aria-hidden="true"]'),
                    node => node.contains(enableAccessibilityButton)
                );

                if (!isButtonHidden) {
                    e.preventDefault();
                    ref.current.focus();
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [ref]);
}

function appendAccessibeScript() {
    const script = document.createElement('script');
    const parentElement = !document.body
        ? document.querySelector('head')
        : document.body;
    script.src = 'https://acsbap.com/apps/app/assets/js/acsb.js';
    script.async = script.defer = true;
    script.onload = function() {
        // eslint-disable-next-line no-undef
        window.acsbJS.init({
            statementLink: '',
            feedbackLink: '',
            footerHtml: 'AccessibilityFooter',
            hideMobile: true,
            hideTrigger: false,
            language: 'en',
            position: 'left',
            leadColor: '#146ff8',
            triggerColor: '#146ff8',
            triggerRadius: '50%',
            triggerPositionX: 'left',
            triggerPositionY: 'bottom',
            triggerIcon: 'default',
            triggerSize: 'medium',
            triggerOffsetX: 20,
            triggerOffsetY: 20,
            mobile: {
                triggerSize: 'small',
                triggerPositionX: 'left',
                triggerPositionY: 'center',
                triggerOffsetX: 0,
                triggerOffsetY: 0,
                triggerRadius: '0',
            },
        });
    };
    parentElement.appendChild(script);
}

const StyledButton = styled.button`
    border-radius: 5px;
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    font: 14px/1.2 ${FontFamily.SharpSansSemibold};
    left: 20px;
    padding: 12px 25px;
    position: fixed;
    top: -120px;
    transition: top 0.2s linear;
    z-index: 10;

    :focus {
        top: 20px;
    }
`;
